/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import ActionConstants from '../constants';

const CLEARED_REQUEST_ID = '@assistedVkyc/CLEARED_REQUEST';

const defaultAsyncState = {
  isLoading: false,
  isSuccess: false,
  isFailure: false,
  errorCode: 0,
  data: null,
  errorMessage: '',
  activeRequestId: '',
};

export const assistedVkycInitialState = {
  statusState: { ...defaultAsyncState },
  kycDataState: { ...defaultAsyncState },
  updateSignatoryState: { ...defaultAsyncState },
  initiateState: { ...defaultAsyncState },
};

const requestState = (currentState, action, preserveData = false) => ({
  ...defaultAsyncState,
  isLoading: true,
  data: preserveData ? currentState.data : null,
  activeRequestId: action.payload?.requestId || '',
});
const successState = (action) => ({
  ...defaultAsyncState,
  isSuccess: true,
  data: action.payload?.body ?? null,
  activeRequestId: action.payload?.extra?.requestId || '',
});
const failureState = (action) => {
  const body = action.payload?.body || {};
  return {
    ...defaultAsyncState,
    isFailure: true,
    errorCode: body.code || body.error_code || body.statusCode || 0,
    errorMessage:
      body.error_details?.errorMessage ||
      body.error_message ||
      body.errorMessage ||
      body.message ||
      '',
    activeRequestId: action.payload?.extra?.requestId || '',
  };
};

const isStaleResponse = (currentState, action): boolean =>
  Boolean(
    currentState.activeRequestId &&
      currentState.activeRequestId !== action.payload?.extra?.requestId
  );

const applyTerminalState = (state, stateKey, action, stateFactory) => {
  if (isStaleResponse(state[stateKey], action)) return state;
  return { ...state, [stateKey]: stateFactory(action) };
};

const clearedState = () => ({
  ...defaultAsyncState,
  activeRequestId: CLEARED_REQUEST_ID,
});

const assistedVkycReducer = (state = assistedVkycInitialState, action) => {
  switch (action.type) {
    case ActionConstants.GET_ASSISTED_VKYC_STATUS_REQUEST:
      return {
        ...state,
        statusState: requestState(state.statusState, action, true),
      };
    case ActionConstants.GET_ASSISTED_VKYC_STATUS_SUCCESS:
      return applyTerminalState(state, 'statusState', action, successState);
    case ActionConstants.GET_ASSISTED_VKYC_STATUS_FAILURE:
      return applyTerminalState(state, 'statusState', action, failureState);

    case ActionConstants.GET_ASSISTED_KYC_DATA_REQUEST:
      return {
        ...state,
        kycDataState: requestState(state.kycDataState, action),
      };
    case ActionConstants.GET_ASSISTED_KYC_DATA_SUCCESS:
      return applyTerminalState(state, 'kycDataState', action, successState);
    case ActionConstants.GET_ASSISTED_KYC_DATA_FAILURE:
      return applyTerminalState(state, 'kycDataState', action, failureState);

    case ActionConstants.UPDATE_ASSISTED_SIGNATORY_DETAILS_REQUEST:
      return {
        ...state,
        updateSignatoryState: requestState(state.updateSignatoryState, action),
      };
    case ActionConstants.UPDATE_ASSISTED_SIGNATORY_DETAILS_SUCCESS:
      return applyTerminalState(
        state,
        'updateSignatoryState',
        action,
        successState
      );
    case ActionConstants.UPDATE_ASSISTED_SIGNATORY_DETAILS_FAILURE:
      return applyTerminalState(
        state,
        'updateSignatoryState',
        action,
        failureState
      );

    case ActionConstants.INITIATE_ASSISTED_VKYC_REQUEST:
      return {
        ...state,
        initiateState: requestState(state.initiateState, action),
      };
    case ActionConstants.INITIATE_ASSISTED_VKYC_SUCCESS:
      return applyTerminalState(state, 'initiateState', action, successState);
    case ActionConstants.INITIATE_ASSISTED_VKYC_FAILURE:
      return applyTerminalState(state, 'initiateState', action, failureState);

    case ActionConstants.CLEAR_ASSISTED_VKYC_STATE:
      return {
        statusState: clearedState(),
        kycDataState: clearedState(),
        updateSignatoryState: clearedState(),
        initiateState: clearedState(),
      };
    default:
      return state;
  }
};

export default assistedVkycReducer;
